/**
 * Admin → Schemas.
 *
 * Backend: `/admin/schemas` (app/routers/admin_schemas.py). Two populations in
 * one list, told apart by `_source`:
 *
 *   code_default   — an extraction target class from canonical_models/, shown as
 *                    it exists in code. Editing it CREATES a customization.
 *   customized     — a stored override of one of those, versioned on each PUT.
 *                    Deleting the customization hands the docType back to the
 *                    code default (it does not delete the class).
 *   los_canonical  — an LOS writeback model (PortalBorrower, BorrowerEntity, …).
 *                    READ-ONLY: the backend 400s on create/update against these.
 *
 * The field-mapping view is the useful cross-check: for an extraction target it
 * shows the LOS fields that docType can populate and the FieldSource tier
 * (primary / fallback) it occupies; for an LOS canonical class it shows which
 * docTypes can source each field.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DataObjectOutlined from "@mui/icons-material/DataObjectOutlined";
import RefreshRounded from "@mui/icons-material/Refresh";
import { useSnackbar } from "notistack";

import { AdminGrid } from "../shared/adminGrid";
import SearchInput from "../../../components/inputs/SearchInput";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { adminSchemasApi } from "../../../api/adminPlatformApi";
import { AdminCard, AdminEmpty, AdminScreenShell } from "../shared/AdminScreenShell";
import SchemaDetailDrawer from "./SchemaDetailDrawer";

const SOURCE_STYLES = {
  code_default: { bg: "#f1f5f9", fg: "#475569", label: "code default" },
  customized: { bg: "#e0e7ff", fg: "#3730a3", label: "customized" },
  los_canonical: { bg: "#fef3c7", fg: "#92400e", label: "LOS canonical" },
};

export default function AdminSchemasScreen() {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quickFilter, setQuickFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminSchemasApi
      .list()
      .then((r) => setRows(Array.isArray(r) ? r : []))
      .catch((err) => {
        setRows([]);
        enqueueSnackbar(
          err?.response?.data?.detail || "Couldn't load schemas",
          { variant: "error" },
        );
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(() => {
    const t = { code_default: 0, customized: 0, los_canonical: 0 };
    (rows || []).forEach((r) => {
      if (t[r._source] != null) t[r._source] += 1;
    });
    return t;
  }, [rows]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Schema",
        field: "name",
        minWidth: 240,
        flex: 1.4,
        sort: "asc",
      },
      {
        headerName: "Doc group",
        field: "doc_group",
        minWidth: 200,
        flex: 1,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "Source",
        field: "_source",
        width: 150,
        cellRenderer: (p) => {
          const s = SOURCE_STYLES[p.value] || SOURCE_STYLES.code_default;
          return (
            <Chip
              size="small"
              label={s.label}
              sx={{
                bgcolor: s.bg,
                color: s.fg,
                fontWeight: 600,
                fontSize: 11,
                height: 20,
              }}
            />
          );
        },
      },
      {
        headerName: "Fields",
        colId: "fieldCount",
        width: 100,
        valueGetter: (p) => (p.data?.fields || []).length,
      },
      {
        headerName: "Version",
        field: "version",
        width: 100,
        valueFormatter: (p) => (p.value == null ? "—" : `v${p.value}`),
      },
      {
        headerName: "Slug",
        field: "_id",
        minWidth: 200,
        flex: 1,
        valueFormatter: (p) => p.value || "—",
      },
    ],
    [],
  );

  return (
    <AdminScreenShell
      icon={<DataObjectOutlined />}
      title="Schemas"
      count={rows?.length}
      loading={loading}
      subtitle="Extraction target schemas and the read-only LOS canonical writeback models. Customizing a code default stores an override that the extraction pipeline uses in its place; deleting the override restores the code default."
      actions={
        <>
          <SearchInput
            value={quickFilter}
            onChange={setQuickFilter}
            placeholder="Search schemas…"
          />
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={load} disabled={loading}>
                <RefreshRounded />
              </IconButton>
            </span>
          </Tooltip>
        </>
      }
    >
      <Flx g={1} sx={{ mb: 2 }} wrap>
        {Object.entries(SOURCE_STYLES).map(([key, s]) => (
          <Chip
            key={key}
            size="small"
            label={`${totals[key]} ${s.label}`}
            sx={{
              bgcolor: s.bg,
              color: s.fg,
              fontWeight: 600,
              fontSize: 11,
              height: 22,
            }}
          />
        ))}
      </Flx>

      <AdminCard dense>
        {rows && rows.length === 0 ? (
          <AdminEmpty>No schemas returned.</AdminEmpty>
        ) : (
          <AdminGrid rowCount={(rows || []).length} maxRows={14}>
            <AgGridReact
              rowData={rows || []}
              columnDefs={columnDefs}
              defaultColDef={{
                sortable: true,
                resizable: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
              }}
              getRowId={(p) => String(p.data?._id ?? p.data?.name)}
              quickFilterText={quickFilter}
              loading={loading}
              onRowClicked={(e) => setSelected(e.data)}
            />
          </AdminGrid>
        )}
      </AdminCard>

      <Box sx={{ mt: 1 }}>
        <Txt sx={{ fontSize: 12, color: "#94a3b8" }}>
          Click a row to inspect its fields, JSON schema, CU field list and
          FieldSource mapping.
        </Txt>
      </Box>

      <SchemaDetailDrawer
        schema={selected}
        onClose={() => setSelected(null)}
        onChanged={load}
      />
    </AdminScreenShell>
  );
}
